/**
 * Chord Quality Drill — free-practice mode.
 *
 * Hear a chord (arpeggio then block), name its quality.
 * No cadence — chord quality is context-free.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import {
  buildChordQualityExerciseRound,
  DEFAULT_CHORD_QUALITY_EXERCISE_CONFIG,
  chordDrillItemId,
  chordDrillStatsKey,
  CHORD_QUALITY_POOL,
  CHORD_QUALITY_SYMBOLS_ORDERED,
  CHORD_QUALITY_GROUPS,
  CHORD_QUALITY_PRESETS,
  type ChordQualityExerciseConfig,
  type ChordQualityExerciseRound,
  type ChordQualitySymbol,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDrillStats,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { Score } from "@music-app/notation";
import type { VexNote } from "@music-app/notation";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { getChallengeData, recordChallengeAttempt } from "@music-app/persistence";

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── State machine ────────────────────────────────────────────────────────────

type ChordDrillPhase = "config" | "playing" | "awaiting" | "reveal" | "graded" | "done";

interface SessionStat {
  correct: number;
  played: number;
}

interface ChordDrillState {
  phase: ChordDrillPhase;
  config: ChordQualityExerciseConfig;
  sessionLength: number;
  round: ChordQualityExerciseRound | null;
  roundNumber: number;
  correctCount: number;
  selectedQuality: ChordQualitySymbol | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<ChordQualitySymbol, SessionStat>>;
}

type ChordDrillAction =
  | { type: "START"; config: ChordQualityExerciseConfig; sessionLength: number; round: ChordQualityExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; quality: ChordQualitySymbol }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: ChordQualityExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: ChordDrillState = {
  phase: "config",
  config: DEFAULT_CHORD_QUALITY_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedQuality: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function addStat(
  stats: Partial<Record<ChordQualitySymbol, SessionStat>>,
  quality: ChordQualitySymbol,
  correct: boolean | null,
): Partial<Record<ChordQualitySymbol, SessionStat>> {
  const prev = stats[quality] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [quality]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function chordDrillReducer(state: ChordDrillState, action: ChordDrillAction): ChordDrillState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addStat(state.sessionStats, state.round.quality, null),
        };
      }
      return { ...state, phase: "awaiting" };
    }

    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = action.quality === state.round.quality;
      return {
        ...state,
        phase: "graded",
        selectedQuality: action.quality,
        wasCorrect: correct,
        wasTimeout: false,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addStat(state.sessionStats, state.round.quality, correct),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addStat(state.sessionStats, state.round.quality, false),
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedQuality: null,
        wasCorrect: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ChordQualityExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(chordDrillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("chords", listenMode, nodeId);
  const { recordItem } = useExerciseSession("chords", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    setAudioPlayer(player);
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Challenge mode: load previous best on mount.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("chords").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Record response start time when awaiting answer (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  // FSRS write + review record after grading (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = chordDrillItemId(state.round.quality, state.config.inversion);
    const correct = state.wasCorrect === true;
    const responseMs = responseStartRef.current
      ? Date.now() - responseStartRef.current
      : 8000;
    const chosenAnswerId =
      !correct && state.selectedQuality !== null ? state.selectedQuality : null;
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) {
        challengeCorrectRef.current += 1;
        const rating: Rating = responseMs < 5000 ? "good" : "hard";
        void applyReview(itemId, rating);
      } else {
        void applyReview(itemId, "again");
      }
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedQuality, state.config.fsrsEnabled, state.config.inversion, state.config.drillMode, challengeMode, applyReview]);

  // Drill stats write after graded answer (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      chordDrillStatsKey(state.round.quality, state.config.inversion),
      "graded",
      state.wasCorrect ?? false,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Drill stats write after listening-mode reveal (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(
      chordDrillStatsKey(state.round.quality, state.config.inversion),
      "listening",
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(chordDrillItemId(state.round.quality, state.config.inversion));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Challenge mode: record attempt on session done.
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const correct = challengeCorrectRef.current;
    void recordChallengeAttempt({
      challengeKey: "chords",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Challenge mode: auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const id = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  const playRound = useCallback(
    async (round: ChordQualityExerciseRound, drillMode: ChordQualityExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);

      if (drillMode === "listening") {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
        await player.play(round.teachAudio);
      }

      recordItem(chordDrillItemId(round.quality, state.config.inversion));
      dispatch({ type: "PLAYBACK_DONE" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordItem, state.config.inversion],
  );

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const startDrill = useCallback(
    (config: ChordQualityExerciseConfig, sessionLength: number) => {
      const round = buildChordQualityExerciseRound(config, nextRoundSeed());
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildChordQualityExerciseRound(state.config, nextRoundSeed());
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound]);

  const handleSelect = useCallback(
    (quality: ChordQualitySymbol) => {
      if (!state.round || state.phase !== "awaiting") return;
      const correct = quality === state.round.quality;

      dispatch({ type: "SELECT", quality });

      if (uiSoundsEnabled) {
        if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
        uiSoundRef.current.play(correct ? "correct" : "wrong");
      }

      if (!correct && state.round.teachAudio) {
        const teachPlan = state.round.teachAudio;
        const player = playerRef.current;
        if (player) {
          window.setTimeout(() => { void player.play(teachPlan); }, 600);
        }
      }
    },
    [state.round, state.phase, uiSoundsEnabled],
  );

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if (state.phase === "graded" || state.phase === "reveal") {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextRound();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, nextRound, replay]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <ChordQualityExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <ChordQualityExerciseCoachStart itemCount={coachItemCount} onStart={startDrill} />;
    return <ChordDrillConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <ChordDrillSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <ChordDrillLoop
      state={state}
      player={audioPlayer}
      listenMode={listenMode}
      challengeMode={challengeMode}
      practiceMode={practiceMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onNext={nextRound}
      onReplay={replay}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Coached listen auto-start ───────────────────────────────────────────────

function ChordQualityExerciseCoachStart({
  itemCount,
  onStart,
}: {
  itemCount: number;
  onStart: (config: ChordQualityExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CHORD_QUALITY_EXERCISE_CONFIG, drillMode: "listening", fsrsEnabled: false }, itemCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function ChordQualityExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: ChordQualityExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CHORD_QUALITY_EXERCISE_CONFIG, drillMode: "graded", fsrsEnabled: false }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Listen mode auto-start ───────────────────────────────────────────────────

// ─── Config persistence ───────────────────────────────────────────────────────

const CHORD_DRILL_LS_KEY = "chord-quality-drill-config-v2";

interface ChordDrillSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  voicing: ChordQualityExerciseConfig["voicing"];
  inversion: ChordQualityExerciseConfig["inversion"];
  selectedQualities: ChordQualitySymbol[];
}

const CHORD_DRILL_DEFAULTS: ChordDrillSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  voicing: "arpeggiated",
  inversion: "root-position",
  selectedQualities: [...CHORD_QUALITY_PRESETS.triads],
};

function loadChordDrillConfig(): ChordDrillSavedConfig {
  try {
    const raw = localStorage.getItem(CHORD_DRILL_LS_KEY);
    if (raw) return { ...CHORD_DRILL_DEFAULTS, ...(JSON.parse(raw) as Partial<ChordDrillSavedConfig>) };
  } catch { /* ignore */ }
  return CHORD_DRILL_DEFAULTS;
}

function saveChordDrillConfig(cfg: ChordDrillSavedConfig): void {
  try { localStorage.setItem(CHORD_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Config screen ────────────────────────────────────────────────────────────

// Preselect the chord qualities for the lesson's node when arriving via
// "Practice this" (`?nodeId=…`) — triads / +sevenths / all — so a seventh-chord
// lesson doesn't open on the persisted triads-only default.
function chordNodeConfigPatch(nodeId: string | undefined): Partial<ChordDrillSavedConfig> {
  switch (nodeId) {
    case "triad-quality":
      return { selectedQualities: [...CHORD_QUALITY_PRESETS.triads] };
    case "seventh-chord-quality":
      return { selectedQualities: [...CHORD_QUALITY_PRESETS.triadsAndSevenths] };
    case "extended-chords-9":
    case "extended-chords-11-13":
    case "altered-dominants":
      return { selectedQualities: [...CHORD_QUALITY_PRESETS.all] };
    default:
      return {};
  }
}

interface ChordDrillConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: ChordQualityExerciseConfig, sessionLength: number) => void;
}

function ChordDrillConfigScreen({ mode, onStart }: ChordDrillConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const [initConfig] = useState(() => ({ ...loadChordDrillConfig(), ...chordNodeConfigPatch(nodeId) }));

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );
  const modeColor = PICKER_MODE_COLOR[mode];
  const [voicing, setVoicing] = useState<ChordQualityExerciseConfig["voicing"]>(initConfig.voicing);
  const [inversion, setInversion] = useState<ChordQualityExerciseConfig["inversion"]>(initConfig.inversion);
  const [selectedQualities, setSelectedQualities] = useState<Set<ChordQualitySymbol>>(
    () => new Set(initConfig.selectedQualities),
  );

  const [historyStats, setHistoryStats] = useState<Partial<Record<string, DrillStatsRecord | null>>>({});

  // Load history stats for checked qualities.
  useEffect(() => {
    const qualities = Array.from(selectedQualities);
    void Promise.all(
      qualities.map((q) =>
        getDrillStats(chordDrillStatsKey(q, inversion)).then(
          (s) => [chordDrillStatsKey(q, inversion), s ?? null] as const,
        ),
      ),
    ).then((entries) => setHistoryStats(Object.fromEntries(entries)));
  }, [selectedQualities, inversion]);

  // Persist config on change.
  useEffect(() => {
    saveChordDrillConfig({
      sessionLength, mode, voicing, inversion,
      selectedQualities: Array.from(selectedQualities),
    });
  }, [sessionLength, mode, voicing, inversion, selectedQualities]);

  const applyPreset = (preset: keyof typeof CHORD_QUALITY_PRESETS) => {
    setSelectedQualities(new Set(CHORD_QUALITY_PRESETS[preset]));
  };

  const toggleQuality = (sym: ChordQualitySymbol) => {
    setSelectedQualities((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) {
        if (next.size === 1) return prev;
        next.delete(sym);
      } else {
        next.add(sym);
      }
      return next;
    });
  };

  const handleStart = () => {
    if (selectedQualities.size === 0) return;
    onStart(
      {
        qualities: CHORD_QUALITY_SYMBOLS_ORDERED.filter((s) => selectedQualities.has(s)),
        voicing,
        inversion,
        drillMode: mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice",
      },
      sessionLength,
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Chord Quality Recognition"
        subhead="Hear a chord — arpeggio then block — name its quality."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/chords" />
      <ExerciseLessonLink nodeIds={["triad-quality", "seventh-chord-quality", "extended-chords-9", "extended-chords-11-13", "altered-dominants", "slash-chords"]} />

      {/* Session settings */}
      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={!showCustomLength && sessionLength === n}
                onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                className={`px-4 py-2 text-sm border transition-colors ${
                  !showCustomLength && sessionLength === n
                    ? ""
                    : "bg-paper text-ink border-ink/20 hover:border-ink"
                }`}
                style={!showCustomLength && sessionLength === n ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength
                  ? ""
                  : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        <ConfigRow label="Voicing">
          <NativeSelect
            value={voicing}
            onChange={(v) => setVoicing(v as ChordQualityExerciseConfig["voicing"])}
            options={[
              { value: "arpeggiated", label: "Arpeggiated  (arp → block)" },
              { value: "blocked",     label: "Blocked  (block only)" },
              { value: "mixed",       label: "Mixed  (random each round)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Inversion">
          <NativeSelect
            value={inversion}
            onChange={(v) => setInversion(v as ChordQualityExerciseConfig["inversion"])}
            options={[
              { value: "root-position",   label: "Root position  (root in bass)" },
              { value: "all-inversions",  label: "All inversions  (harder)" },
            ]}
          />
        </ConfigRow>
      </div>

      {/* Chord pool */}
      <div className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Chord pool
        </p>

        <div className="flex gap-2 mb-4 flex-wrap">
          {(["triads", "triadsAndSevenths", "all"] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => applyPreset(preset)}
              className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
            >
              {preset === "triads" ? "Triads" : preset === "triadsAndSevenths" ? "Triads + 7ths" : "All"}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelectedQualities(new Set())}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-inkFaded hover:border-ink hover:text-ink transition-colors"
          >
            None
          </button>
        </div>

        <div className="border border-ink/10">
          {CHORD_QUALITY_GROUPS.map((group) => (
            <div key={group.label} className="border-b border-ink/10 last:border-b-0">
              <div className="px-4 py-1.5 bg-paper border-b border-ink/5">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">
                  {group.label}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3">
                {group.symbols.map((sym) => {
                  const checked = selectedQualities.has(sym);
                  const info = CHORD_QUALITY_POOL[sym];
                  return (
                    <label
                      key={sym}
                      className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors ${
                        checked ? "text-ink" : "text-inkFaded"
                      } hover:bg-ink/5`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleQuality(sym)}
                        className="accent-ink w-3.5 h-3.5 shrink-0"
                        aria-label={`Include ${info.longName}`}
                      />
                      <span className="font-mono text-xs font-medium">{info.shortSymbol}</span>
                      <span className="font-serif italic text-xs">{info.longName}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History table */}
      <ChordHistoryTable
        qualities={CHORD_QUALITY_SYMBOLS_ORDERED.filter((s) => selectedQualities.has(s))}
        inversion={inversion}
        historyStats={historyStats}
      />

      <button
        type="button"
        onClick={handleStart}
        disabled={selectedQualities.size === 0}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface ChordDrillLoopProps {
  state: ChordDrillState;
  player: AudioPlayer | null;
  listenMode: boolean;
  challengeMode: boolean;
  practiceMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (quality: ChordQualitySymbol) => void;
  onNext: () => void;
  onReplay: () => void;
  onStop: () => void;
  from?: string;
}

function ChordDrillLoop({
  state,
  listenMode,
  challengeMode,
  practiceMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onNext,
  onReplay,
  onStop,
  from,
}: ChordDrillLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, selectedQuality, wasCorrect, wasTimeout } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isReveal   = phase === "reveal";
  const isGraded   = phase === "graded";

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  const qualityButtons = CHORD_QUALITY_SYMBOLS_ORDERED.filter((s) =>
    config.qualities.includes(s),
  );
  const useGrid3 = qualityButtons.length >= 9;

  // Build VexFlow notes: root only during awaiting, full chord on reveal/graded.
  const scoreNotes: VexNote[] | null = round
    ? isReveal || isGraded
      ? [{ keys: round.playedMidis.map(midiToVexKey), duration: "w" as const }]
      : [{ keys: [midiToVexKey(round.rootMidi)], duration: "w" as const }]
    : null;

  const scoreAriaLabel = round
    ? isReveal || isGraded
      ? `${CHORD_QUALITY_POOL[round.quality].longName} chord (${CHORD_QUALITY_POOL[round.quality].shortSymbol})`
      : `Root note: ${midiToNoteName(round.rootMidi)}`
    : "Chord staff";

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Chord Quality Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {config.inversion === "all-inversions" ? "All inversions" : "Root position"}
        {" · "}
        {config.voicing === "mixed" ? "Mixed voicing" : config.voicing}
      </p>

      {/* Progress bar */}
      <div className="w-full h-0.5 bg-ink/10 mb-2" role="progressbar" aria-valuenow={roundNumber} aria-valuemin={1} aria-valuemax={sessionLength}>
        <div className="h-full transition-all duration-300" style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }} />
      </div>

      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        {isPlaying && <span className="animate-pulse">Playing…</span>}
      </div>

      {/* Chord notation */}
      {scoreNotes && (
        <div className="mb-4">
          <Score notes={scoreNotes} width={200} height={130} aria-label={scoreAriaLabel} />
        </div>
      )}

      {/* Prompt */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">Listen…</p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            What chord quality did you hear?
          </p>
        )}
        {(isReveal || isGraded) && round && (
          <div aria-live="polite">
            {isReveal && (
              <p className="font-serif italic text-xl text-ink">
                That was{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CHORD_QUALITY_POOL[round.quality].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                Correct —{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CHORD_QUALITY_POOL[round.quality].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && !wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                {wasTimeout ? "Time! " : "The answer was "}{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CHORD_QUALITY_POOL[round.quality].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && !challengeMode && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1"
                 style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Answer buttons */}
      <div className="mb-6">
        <div className={`grid gap-2 mb-2 ${useGrid3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {qualityButtons.map((sym) => {
            const info = CHORD_QUALITY_POOL[sym];
            const isCorrect  = sym === round?.quality;
            const isSelected = sym === selectedQuality;
            const showCorrect = (isGraded || isReveal) && isCorrect;
            const showWrong   = isGraded && isSelected && !isCorrect;
            const disabled    = isPlaying || isReveal || isGraded;

            return (
              <button
                key={sym}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(sym)}
                aria-pressed={isSelected && isAwaiting}
                aria-label={`${info.longName}, ${info.shortSymbol}`}
                className={`px-3 py-3 border text-left transition-colors ${
                  showCorrect
                    ? "bg-success/10 border-success text-success"
                    : showWrong
                    ? "bg-danger/10 border-danger text-danger"
                    : disabled
                    ? "bg-paper border-ink/10 text-inkFaded"
                    : "bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5"
                }`}
              >
                <div className="font-serif text-sm font-medium leading-tight">{info.longName}</div>
                <div className="font-mono text-[10px] text-inkFaded mt-0.5">{info.shortSymbol}</div>
              </button>
            );
          })}
        </div>
        {isAwaiting && <p className="mt-2 font-mono text-[10px] text-inkFaded">R to replay</p>}
        {(isReveal || isGraded) && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the chord"
          >
            ♩ Replay
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface ChordDrillSummaryProps {
  config: ChordQualityExerciseConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<ChordQualitySymbol, SessionStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function ChordDrillSummary({
  config,
  sessionLength,
  correctCount,
  sessionStats,
  onAgain,
  onStop,
}: ChordDrillSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="chord quality"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Correct</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}<span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {Object.keys(sessionStats).length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">By quality</p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Quality</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>}
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>}
            </div>
            {CHORD_QUALITY_SYMBOLS_ORDERED.filter((s) => sessionStats[s] !== undefined).map((sym) => {
              const stat = sessionStats[sym]!;
              const pct = stat.played > 0 && stat.correct > 0
                ? Math.round((stat.correct / stat.played) * 100)
                : stat.played > 0 ? 0 : null;
              const info = CHORD_QUALITY_POOL[sym];
              return (
                <div key={sym} className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
                  <div>
                    <span className="font-serif text-sm text-ink">{info.longName}</span>
                    <span className="font-mono text-xs text-inkFaded ml-2">{info.shortSymbol}</span>
                  </div>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button type="button" onClick={onAgain} className={buttonClasses("primary")}>
          Drill again →
        </button>
        <button type="button" onClick={onStop} className={buttonClasses("secondary")}>
          Change settings
        </button>
        <Link to="/exercise" className={buttonClasses("secondary")}>
          All drills
        </Link>
        <Link to="/stats" className={buttonClasses("secondary")}>
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["triad-quality", "seventh-chord-quality", "extended-chords-9", "extended-chords-11-13", "altered-dominants", "slash-chords"]} />
      </div>
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

interface ChordHistoryTableProps {
  qualities: readonly ChordQualitySymbol[];
  inversion: ChordQualityExerciseConfig["inversion"];
  historyStats: Partial<Record<string, DrillStatsRecord | null>>;
}

function ChordHistoryTable({ qualities, inversion, historyStats }: ChordHistoryTableProps): ReactNode {
  const rows = qualities.map((q) => ({
    quality: q,
    stats: historyStats[chordDrillStatsKey(q, inversion)] ?? null,
  }));

  const hasAnyData = rows.some((r) => r.stats && r.stats.played > 0);
  const [open, setOpen] = useState(false);
  if (!hasAnyData) return null;
  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      <div className={open ? "border border-ink/10" : "hidden"}>
        <div className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2 border-b border-ink/10">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Quality</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
        </div>
        {rows
          .filter((r) => r.stats && r.stats.played > 0)
          .map(({ quality, stats }) => {
            const s = stats!;
            const listened = Math.max(0, s.played - s.correct - s.errors);
            const graded   = s.correct + s.errors;
            const pct      = graded > 0 ? Math.round((s.correct / graded) * 100) : null;
            const info = CHORD_QUALITY_POOL[quality];
            return (
              <div key={quality} className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
                <div>
                  <span className="font-serif text-sm text-ink">{info.longName}</span>
                  <span className="font-mono text-xs text-inkFaded ml-2">{info.shortSymbol}</span>
                </div>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{listened}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{graded}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{graded > 0 ? s.correct : "—"}</span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{pct !== null ? `${pct}%` : "—"}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ─── VexFlow helpers ──────────────────────────────────────────────────────────

const SEMI_TO_VEX: Record<number, { letter: string; acc: string }> = {
  0:  { letter: "c", acc: "" }, 1:  { letter: "d", acc: "b" },
  2:  { letter: "d", acc: "" }, 3:  { letter: "e", acc: "b" },
  4:  { letter: "e", acc: "" }, 5:  { letter: "f", acc: "" },
  6:  { letter: "f", acc: "#" }, 7: { letter: "g", acc: "" },
  8:  { letter: "a", acc: "b" }, 9:  { letter: "a", acc: "" },
  10: { letter: "b", acc: "b" }, 11: { letter: "b", acc: "" },
};

function midiToVexKey(midi: number): string {
  const semi = ((midi % 12) + 12) % 12;
  const oct  = Math.floor(midi / 12) - 1;
  const { letter, acc } = SEMI_TO_VEX[semi]!;
  return `${letter}${acc}/${oct}`;
}

const MIDI_NOTE_NAMES = ["C", "D♭", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];

function midiToNoteName(midi: number): string {
  const semi = ((midi % 12) + 12) % 12;
  const oct  = Math.floor(midi / 12) - 1;
  return `${MIDI_NOTE_NAMES[semi]}${oct}`;
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">{label}</p>
      {children}
    </div>
  );
}

function NativeSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">▾</span>
    </div>
  );
}
